import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Route, useHistory, Link } from 'react-router-dom';
import moment from 'moment';
import { LoadingSpinner, styles, internationalizeDate } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { chatamsBlue } from '../../styles/theme';
import { hoverColor } from '../../components/NavCard';
import { Ballcap, useOktaQuery } from '../../data';
import usePlayerSelector from '../../utils/usePlayerSelector';
import BallcapSessionDisplay from './BallcapSessionDisplay';
import { MenuItemKey, MenuItemValue } from '../MechanicsAndWorkload/Menu';

const StyledBallcapReport = styled.div`
    margin: -${styles.theme.margin.body};
    height: calc(100% + 2 * ${styles.theme.margin.body});
    display: flex;
    flex-direction: column;
    position: relative;
`;
const StyledBallcapSessionSelector = styled.div`
    flex-grow: 0;
    width: 225px;
    min-width: 225px;
    border-right: 1px solid ${styles.theme.border.alto};
    background-color: white;
    p, a.h5 {
        background-color: white;
        padding: 15px 10px 7.5px;
        margin: 0;
    }
    a.h5 {
        display: block;
        padding-top: 7.5px;
        padding-left: 15px;
        color: ${chatamsBlue};
        &:hover {
            text-decoration: none;
            background-color: ${hoverColor};
        }
        div {
            font-size: 80%;
            font-weight: 400;
            color: #333;
            margin-top: 0.5em;
        }
    }
    a.active {
        &.h5, &.h5:hover {
            background-color: #CCE5FF;
        }
    }
    p {
        color: ${styles.theme.fontColors.doveGray};
        text-transform: uppercase;
        font-size: 1.15em;
    }
    p, a.h5 {
        border-bottom: 1px solid ${styles.theme.border.alto};
    }
`;
const StyledBallcapSessionContent = styled.div`
    flex-grow: 1;
`;
const StyledBallcapReportContainer = styled.div`
    flex-grow: 1;
    display: flex;
    overflow-y: hidden;
    > ${StyledBallcapSessionSelector}, > ${StyledBallcapSessionContent} {
        overflow-y: auto;
    }
    > ${StyledBallcapSessionSelector} {
        overflow-x: hidden;
    }
`;
const StyledDaySummary = styled.button`
    background: none;
    color: inherit;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    outline: inherit;
    padding: 10px;
    border-bottom: 1px solid ${styles.theme.border.alto};
    width: 100%;
    text-align: left;
    background-color: ${(props) => (props.$isSelected ? '#E6F5FF' : 'white')}
`;
const StyledDaySummaryTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${styles.theme.fontColors.darkBlue};
`;
const StyledSummaryData = styled.div`
    font-size: 13px;
    margin-top: 2px;
`;

const BallcapSessions = ({ match: { url, path } }) => {
    const navigate = useHistory();
    const [selectedDaySummaryId, setSelectedDaySummaryId] = useState(null);
    const { t: translate } = useTranslation();
    const { PlayerID } = usePlayerSelector();
    const prefix = `${url}/`;
    const { data: sessions, isLoading: loadingSessions } = useOktaQuery(
        ['ballcap', PlayerID],
        (accessToken) => Ballcap.getPlayerSessions(accessToken, PlayerID),
        { onSuccess: (data) => {
            const navStr = data?.length > 0 ? `${prefix}${data[0].SessionID}` : prefix;
            navigate.push(navStr);
            if (data?.length > 0) {
                setSelectedDaySummaryId(data[0].SessionID);
            }
        } },
    );

    return (
        <StyledBallcapReport>
            <StyledBallcapReportContainer>
                <StyledBallcapSessionSelector>
                    <div>
                        <p>{translate('sections.ballcap.ballcap-sessions')}</p>
                        {loadingSessions && (<LoadingSpinner />)}
                        {!loadingSessions && sessions?.length > 0 && sessions.map((session) => (
                            <Link key={session.SessionID} to={`${prefix}${session.SessionID}`}>
                                <StyledDaySummary
                                    key={session.SessionID}
                                    $isSelected={selectedDaySummaryId === session.SessionID}
                                    onClick={() => setSelectedDaySummaryId(session.SessionID)}
                                >
                                    <div className="d-flex justify-content-between">
                                        <StyledDaySummaryTitle>
                                            {internationalizeDate(translate, moment(`${session.CreatedDate}`), false, true, true, true)}
                                        </StyledDaySummaryTitle>
                                    </div>
                                    <StyledSummaryData>
                                        <div className="d-flex">
                                            <div className="d-flex" style={{ width: '65%' }}>
                                                <MenuItemKey>{translate('sections.ballcap.type')}:&nbsp;</MenuItemKey>
                                                <MenuItemValue>{session.SessionTypeName}</MenuItemValue>
                                            </div>
                                            <div className="d-flex">
                                                <MenuItemKey>{translate('baseball.pitches_other')}:&nbsp;</MenuItemKey>
                                                <MenuItemValue>{session.NumPitches}</MenuItemValue>
                                            </div>
                                        </div>
                                        <div className="d-flex">
                                            <div className="d-flex">
                                                <MenuItemKey>{translate('common.at')}:&nbsp;</MenuItemKey>
                                                <MenuItemValue>{session.LocationName}</MenuItemValue>
                                            </div>
                                        </div>
                                    </StyledSummaryData>
                                </StyledDaySummary>
                            </Link>
                        ))}
                    </div>
                </StyledBallcapSessionSelector>
                <StyledBallcapSessionContent>
                    <Route path={path} exact>
                        <div style={{ marginTop: '10px', marginLeft: '10px' }}>
                            {!loadingSessions && sessions?.length > 0 && (<h4>↝ {translate('common.select-a-session')}</h4>)}
                            {!loadingSessions && sessions?.length === 0 && (<h4>{translate('sections.ballcap.no-sessions-found')}</h4>)}
                        </div>
                    </Route>
                    <Route
                        path={`${path}/:sessionId(\\d+)`}
                        render={(routeProps) => {
                            const sessionid = parseInt(routeProps.match.params.sessionId);
                            const session = sessions?.find((s) => s.SessionID === sessionid);
                            return session && (
                                <BallcapSessionDisplay playerId={PlayerID} sessionId={session.SessionID} sessionDate={session.CreatedDate} />
                            );
                        }}
                    />
                </StyledBallcapSessionContent>
            </StyledBallcapReportContainer>
        </StyledBallcapReport>
    );
};

BallcapSessions.propTypes = {
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }).isRequired,
    match: PropTypes.shape({
        path: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
    }).isRequired,
};

export default BallcapSessions;
